import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ProgressBar,
} from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';
import { T } from '@tolgee/react';
import { clamp } from '~/helper/number';
import { clsx } from 'clsx';

interface Props {
  readonly propertyData?: {
    readonly buildingYear?: string;
    readonly livingArea?: string;
    readonly numberOfRooms?: string;
    readonly numberOfBathrooms?: string;
    readonly numberOfIndoorParkingSpaces?: string;
    readonly numberOfOutdoorParkingSpaces?: string;
  };
  readonly propertyCondition: Record<string, unknown>;
  readonly propertyQuality: Record<string, unknown>;
  readonly link: string;
  readonly showDemoWidgetContent?: boolean;
}

export function PropertyProgressWidget(props: Props) {
  const baseKeys = [
    'buildingYear',
    'livingArea',
    'numberOfRooms',
    'numberOfBathrooms',
    'numberOfIndoorParkingSpaces',
    'numberOfOutdoorParkingSpaces',
  ];
  const recordedBaseProperties = props.showDemoWidgetContent
    ? 5
    : countDefinedProperties(props.propertyData, baseKeys);

  const qualityKeys = [
    'quality_bathrooms',
    'quality_windows',
    'quality_flooring',
    'quality_masonry',
    'quality_kitchen',
  ];
  const recordedQualityProperties = props.showDemoWidgetContent
    ? 3
    : countDefinedProperties(props.propertyQuality, qualityKeys);

  const conditionKeys = [
    'condition_bathrooms',
    'condition_windows',
    'condition_flooring',
    'condition_masonry',
    'condition_kitchen',
  ];
  const recordedConditionProperties = props.showDemoWidgetContent
    ? 2
    : countDefinedProperties(props.propertyCondition, conditionKeys);

  const totalProgress =
    ((recordedBaseProperties +
      recordedQualityProperties +
      recordedConditionProperties) /
      (baseKeys.length + qualityKeys.length + conditionKeys.length)) *
    100;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            <T>layout_objectinfo</T>
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {Math.round(totalProgress)}%
            </span>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <svg
                className="h-4 w-4 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent
        className={clsx('flex-1 flex flex-col pt-0', {
          'opacity-50': props.showDemoWidgetContent,
        })}
      >
        <div className="space-y-4 flex-1">
          <PropertyInfoProgress
            progressFraction={recordedBaseProperties / baseKeys.length}
            title="widget_propertyinfo_base_data"
            icon="home"
            completedCount={recordedBaseProperties}
            totalCount={baseKeys.length}
          />
          <PropertyInfoProgress
            progressFraction={recordedQualityProperties / qualityKeys.length}
            title="widget_propertyinfo_finishing_standard"
            icon="star"
            completedCount={recordedQualityProperties}
            totalCount={qualityKeys.length}
          />
          <PropertyInfoProgress
            progressFraction={
              recordedConditionProperties / conditionKeys.length
            }
            title="widget_propertyinfo_condition"
            icon="check"
            completedCount={recordedConditionProperties}
            totalCount={conditionKeys.length}
          />
        </div>

        {!props.showDemoWidgetContent && (
          <AppLink
            to={`${props.link}#evaluationPropertyInformation` as any}
            className="mt-6 inline-block"
          >
            <button className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 font-medium text-sm flex items-center justify-center gap-2">
              <T>widget_propertyinfo_button_text</T>
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
          </AppLink>
        )}
      </CardContent>
    </Card>
  );
}

interface ProgressProps {
  readonly title: string;
  readonly progressFraction: number;
  readonly icon: 'home' | 'star' | 'check';
  readonly completedCount: number;
  readonly totalCount: number;
  readonly values?: { key: string; value: string }[];
  readonly valuePrefix?: string;
  readonly valueSuffix?: string;
  readonly valueTranslationPrefix?: string;
}

function PropertyInfoProgress(props: ProgressProps) {
  const progress = clamp(props.progressFraction, 0, 1);
  const isFull = progress >= 1;
  const isEmpty = progress <= 0;
  const percentage = progress * 100;

  let progressLabel: string;
  if (isFull) {
    progressLabel = 'widget_propertyinfo_fully_recorded';
  } else if (isEmpty) {
    progressLabel = 'widget_propertyinfo_not_recorded';
  } else {
    progressLabel = 'widget_propertyinfo_partially_recorded';
  }

  const getIcon = () => {
    switch (props.icon) {
      case 'home':
        return (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
        );
      case 'star':
        return (
          <svg
            className="h-4 w-4"
            fill="currentColor"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        );
      case 'check':
        return (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
    }
  };

  return (
    <div className="group hover:bg-gray-50 dark:hover:bg-zinc-800 dark:bg-zinc-800/50 dark:hover:bg-zinc-700/50 p-3 rounded-lg transition-colors duration-200 -mx-3">
      <div className="flex items-start gap-3">
        <div
          className={clsx(
            'h-10 w-10 rounded-lg flex items-center justify-center transition-colors duration-200 flex-shrink-0',
            isFull
              ? 'bg-green-100 text-green-600'
              : isEmpty
              ? 'bg-gray-100 dark:bg-zinc-800 text-gray-400 dark:bg-zinc-800 dark:text-zinc-500'
              : 'bg-blue-100 text-blue-600',
          )}
        >
          {getIcon()}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex justify-between items-start gap-2">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              <T keyName={props.title} />
            </h3>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {props.completedCount}/{props.totalCount}
              </div>
              <div className="text-xs text-gray-500 dark:text-zinc-400">
                <T keyName={progressLabel} />
              </div>
            </div>
          </div>
          <ProgressBar
            value={percentage}
            size="sm"
            variant={isFull ? 'success' : 'primary'}
            className="mt-2"
          />
          {props.values && props.values.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {props.values.map(({ key, value }) => (
                <span
                  key={key}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300"
                  title={`${key}: ${value}`}
                >
                  {props.valuePrefix && (
                    <T
                      keyName={`${props.valuePrefix}${key}${
                        props.valueSuffix || ''
                      }`}
                    />
                  )}
                  {!props.valuePrefix && key}
                  <span className="mx-1">:</span>
                  <T
                    keyName={`${
                      props.valueTranslationPrefix || 'model_property_'
                    }${value}`}
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function countDefinedProperties(
  obj: Record<string, unknown> | undefined | null,
  keys: string[],
): number {
  if (!obj) return 0;
  return keys.filter((key) => {
    const value = obj[key];
    return value != null && value !== 'not_defined' && !Number.isNaN(value);
  }).length;
}
